/*

& "Media, links and docs" for one chat on the website (3 Nov 2026, gap A10), the app's chat profile cards and their
& sheets: the newest photos and a row each for Media, Links and Docs; then "Shared in this chat", a row each for
& Listings, Broadcasts and Requirements. A row opens its list (Media, Links and Docs as tabs), newest first, with
& "Load more". Counts are chat/fetch-shared-summary, lists chat/fetch-shared-items (lib/chat/sharedInChatCore.ts).
& A photo, document or link opens in a new tab, a listing its page; a requirement is shown, not opened (the website
& has no page for one requirement yet).

*/

import { ArrowBackOutlined, CloseOutlined } from "@mui/icons-material";
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { useRouter } from "next/router";
import React from "react";

import BeegruButton from "../../../common-components/buttons/BeegruButton";

type Kind = "media" | "links" | "docs" | "properties" | "broadcasts" | "requirements";
type Summary = Record<string, any> & { media_preview: Array<{ id: string; url: string }> };

const ChatSharedDialog = ({
	open,
	onClose,
	chat_id,
	profile_context,
}: {
	open: boolean;
	onClose: () => void;
	chat_id: string;
	profile_context: any;
}) => {
	const router = useRouter();
	const tr = (en: string, ar: string, kn: string): string =>
		router.locale === "ar-AE" ? ar : router.locale === "kn-IN" ? kn : en;
	const LABEL: Record<Kind, string> = {
		media: tr("Media", "الوسائط", "ಮೀಡಿಯಾ"),
		links: tr("Links", "الروابط", "ಲಿಂಕ್‌ಗಳು"),
		docs: tr("Docs", "المستندات", "ದಾಖಲೆಗಳು"),
		properties: tr("Listings", "العقارات", "ಪಟ್ಟಿಗಳು"),
		broadcasts: tr("Broadcasts", "البث", "ಬ್ರಾಡ್‌ಕಾಸ್ಟ್‌ಗಳು"),
		requirements: tr("Requirements", "المتطلبات", "ಅಗತ್ಯಗಳು"),
	};

	const [summary, setSummary] = React.useState<Summary | null>(null);
	const [failed, setFailed] = React.useState<boolean>(false);
	const [kind, setKind] = React.useState<Kind | null>(null);
	const [items, setItems] = React.useState<Array<any>>([]);
	const [cursor, setCursor] = React.useState<string | null>(null);
	const [loading, setLoading] = React.useState<boolean>(false);
	/* A page read failed: said with Try again, not as "No photos yet". */
	const [pageFailed, setPageFailed] = React.useState<boolean>(false);
	/* The list on show: an answer for a tab already left is dropped, so one tab's items never land in another. */
	const listTokenRef = React.useRef<number>(0);

	const post = (route: string, extra: Record<string, any>) =>
		fetch(process.env.PRODUCTION_API_URL + route, {
			method: "POST",
			cache: "no-store",
			headers: { "Content-Type": "text/plain" },
			body: JSON.stringify({
				chat_id,
				is_business_profile: profile_context?.is_business_profile === true,
				business_profile_id: profile_context?.is_business_profile ? profile_context.business_profile_id : undefined,
				...extra,
			}),
		}).then((res) => res.json());

	React.useEffect(() => {
		if (!open) return;
		setSummary(null);
		setFailed(false);
		setKind(null);
		post("chat/fetch-shared-summary", {})
			.then((data) => (data?.status === "success" ? setSummary(data.payload) : setFailed(true)))
			.catch(() => setFailed(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, chat_id]);

	const loadPage = (which: Kind, after: string | null) => {
		const token: number = listTokenRef.current;
		setLoading(true);
		setPageFailed(false);
		post("chat/fetch-shared-items", { kind: which, cursor: after })
			.then((data) => {
				if (token !== listTokenRef.current) return;
				if (data?.status !== "success") {
					setPageFailed(true);
					return;
				}
				setItems((current) => (after ? [...current, ...data.payload.items] : data.payload.items));
				setCursor(data.payload.next_cursor);
			})
			.catch(() => token === listTokenRef.current && setPageFailed(true))
			.finally(() => token === listTokenRef.current && setLoading(false));
	};

	const openList = (which: Kind) => {
		listTokenRef.current += 1;
		setKind(which);
		setItems([]);
		setCursor(null);
		loadPage(which, null);
	};

	const count = (which: Kind): number => Number(summary?.[which + "_count"] || 0);
	const openUrl = (url: string) => url && window.open(url, "_blank", "noopener,noreferrer");
	const dateOf = (value: any): string => (value ? new Date(value).toLocaleDateString(router.locale || "en-IN") : "");

	const Row = ({ which }: { which: Kind }) => (
		<div
			role="button"
			tabIndex={0}
			onClick={() => openList(which)}
			onKeyDown={(event) => event.key === "Enter" && openList(which)}
			style={{ display: "flex", justifyContent: "space-between", padding: "0.75rem 0.25rem", cursor: "pointer", borderBottom: "1px solid rgba(0,0,0,0.06)" }}
		>
			<Typography variant="body2">{LABEL[which]}</Typography>
			<Typography variant="body2" color="text.secondary">{count(which)}</Typography>
		</div>
	);

	const renderItem = (item: any, index: number) => {
		if (kind === "media")
			return (
				<img
					key={item.id || index}
					src={item.url}
					alt=""
					onClick={() => openUrl(item.url)}
					style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: "0.5rem", cursor: "pointer" }}
				/>
			);
		const line = (title: string, sub: string, onClick?: () => void) => (
			<div
				key={(item.id || item.property_id || item.requirement_id || "") + index}
				onClick={onClick}
				style={{ padding: "0.625rem 0.25rem", borderBottom: "1px solid rgba(0,0,0,0.06)", cursor: onClick ? "pointer" : "default", minWidth: 0 }}
			>
				<Typography variant="body2" noWrap>{title}</Typography>
				<Typography variant="caption" color="text.secondary" noWrap component="div">{sub}</Typography>
			</div>
		);
		const by = (item: any) => [item.sender_is_viewer ? tr("You", "أنت", "ನೀವು") : "", dateOf(item.sent_at)].filter(Boolean).join(" · ");
		if (kind === "links") return line(item.display_url || item.url, [item.host, by(item)].filter(Boolean).join(" · "), () => openUrl(item.url));
		if (kind === "docs") return line(item.file_name || tr("Document", "مستند", "ದಾಖಲೆ"), by(item), () => openUrl(item.url));
		if (kind === "requirements")
			return line(
				item.title,
				[item.posted_by_viewer ? tr("Your requirement", "متطلبك", "ನಿಮ್ಮ ಅಗತ್ಯ") : tr("Claimed by you", "طالبت به أنت", "ನೀವು ಕ್ಲೈಮ್ ಮಾಡಿದ್ದು"), item.location_title].filter(Boolean).join(" · "),
			);
		return line(item.title, [item.location_title, item.price_label].filter(Boolean).join(" · "), () =>
			item.slug ? openUrl(`${process.env.PRODUCTION_URL || "/"}${router.locale || "en-IN"}/properties/${item.slug}`) : undefined,
		);
	};

	const mediaTabs: Array<Kind> = ["media", "links", "docs"];
	const empty: Record<Kind, string> = {
		media: tr("No photos yet", "لا توجد صور بعد", "ಇನ್ನೂ ಫೋಟೋಗಳಿಲ್ಲ"),
		links: tr("No links yet", "لا توجد روابط بعد", "ಇನ್ನೂ ಲಿಂಕ್‌ಗಳಿಲ್ಲ"),
		docs: tr("No documents yet", "لا توجد مستندات بعد", "ಇನ್ನೂ ದಾಖಲೆಗಳಿಲ್ಲ"),
		properties: tr("No listings yet", "لا توجد عقارات بعد", "ಇನ್ನೂ ಪಟ್ಟಿಗಳಿಲ್ಲ"),
		broadcasts: tr("No broadcasts yet", "لا يوجد بث بعد", "ಇನ್ನೂ ಬ್ರಾಡ್‌ಕಾಸ್ಟ್‌ಗಳಿಲ್ಲ"),
		requirements: tr("No requirements yet", "لا توجد متطلبات بعد", "ಇನ್ನೂ ಅಗತ್ಯಗಳಿಲ್ಲ"),
	};
	const spinner = <CircularProgress size={22} sx={{ alignSelf: "center", color: "#B05920", margin: "1rem auto", display: "block" }} />;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { direction: router.locale === "ar-AE" ? "rtl" : "ltr" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "1rem", fontWeight: 600 }}>
				{kind ? (
					<IconButton size="small" aria-label={tr("Back", "رجوع", "ಹಿಂದೆ")} onClick={() => {
						listTokenRef.current += 1;
						setLoading(false);
						setKind(null);
					}}>
						<ArrowBackOutlined fontSize="small" />
					</IconButton>
				) : null}
				<span style={{ flex: 1 }}>{kind && !mediaTabs.includes(kind) ? LABEL[kind] : tr("Media, links and docs", "الوسائط والروابط والمستندات", "ಮೀಡಿಯಾ, ಲಿಂಕ್‌ಗಳು ಮತ್ತು ದಾಖಲೆಗಳು")}</span>
				<IconButton size="small" aria-label={tr("Close", "إغلاق", "ಮುಚ್ಚಿ")} onClick={onClose}>
					<CloseOutlined fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{failed ? (
					<Typography variant="body2" color="text.secondary">{tr("Couldn't load this. Please try again.", "تعذر التحميل. يرجى المحاولة مرة أخرى.", "ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.")}</Typography>
				) : !summary ? (
					spinner
				) : kind ? (
					<>
						{mediaTabs.includes(kind) ? (
							<Tabs value={kind} onChange={(_, value) => openList(value)} variant="fullWidth" sx={{ marginBottom: "0.5rem" }}>
								{mediaTabs.map((tab) => (
									<Tab key={tab} value={tab} label={`${LABEL[tab]} (${count(tab)})`} sx={{ textTransform: "none" }} />
								))}
							</Tabs>
						) : null}
						<div style={kind === "media" ? { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.375rem" } : undefined}>
							{items.map(renderItem)}
						</div>
						{loading ? spinner : null}
						{!loading && pageFailed ? (
							<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", padding: "1rem 0" }}>
								<Typography variant="body2" color="text.secondary">
									{tr("Couldn't load this. Please try again.", "تعذر التحميل. يرجى المحاولة مرة أخرى.", "ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.")}
								</Typography>
								<BeegruButton variant="outlined" flavor="primary" size="small" onClick={() => loadPage(kind, items.length > 0 ? cursor : null)}>
									{tr("Try again", "حاول مرة أخرى", "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ")}
								</BeegruButton>
							</div>
						) : null}
						{!loading && !pageFailed && items.length === 0 ? (
							<Typography variant="body2" color="text.secondary" sx={{ padding: "1rem 0", textAlign: "center" }}>{empty[kind]}</Typography>
						) : null}
						{!loading && !pageFailed && cursor ? (
							<BeegruButton variant="outlined" flavor="primary" size="small" onClick={() => loadPage(kind, cursor)} sx={{ margin: "0.75rem auto 0", display: "flex" }}>
								{tr("Load more", "تحميل المزيد", "ಇನ್ನಷ್ಟು ಲೋಡ್ ಮಾಡಿ")}
							</BeegruButton>
						) : null}
					</>
				) : (
					<>
						{summary.media_preview?.length ? (
							<div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.375rem", marginBottom: "0.5rem" }}>
								{summary.media_preview.map((photo) => (
									<img key={photo.id} src={photo.url} alt="" onClick={() => openList("media")} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: "0.5rem", cursor: "pointer" }} />
								))}
							</div>
						) : null}
						{mediaTabs.filter((tab) => count(tab) > 0).map((tab) => <Row key={tab} which={tab} />)}
						{(["properties", "broadcasts", "requirements"] as Array<Kind>).some((tab) => count(tab) > 0) ? (
							<Typography variant="overline" color="text.secondary" component="div" sx={{ marginTop: "1rem" }}>
								{tr("Shared in this chat", "تمت مشاركته في هذه الدردشة", "ಈ ಚಾಟ್‌ನಲ್ಲಿ ಹಂಚಿಕೊಂಡಿದ್ದು")}
							</Typography>
						) : null}
						{(["properties", "broadcasts", "requirements"] as Array<Kind>).filter((tab) => count(tab) > 0).map((tab) => <Row key={tab} which={tab} />)}
						{(["media", "links", "docs", "properties", "broadcasts", "requirements"] as Array<Kind>).every((tab) => count(tab) === 0) ? (
							<Typography variant="body2" color="text.secondary" sx={{ padding: "1rem 0", textAlign: "center" }}>
								{tr("Nothing has been shared in this chat yet.", "لم تتم مشاركة أي شيء في هذه الدردشة بعد.", "ಈ ಚಾಟ್‌ನಲ್ಲಿ ಇನ್ನೂ ಏನನ್ನೂ ಹಂಚಿಕೊಂಡಿಲ್ಲ.")}
							</Typography>
						) : null}
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default ChatSharedDialog;
